// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

import { A11y, Autoplay, Controller, EffectFade, FreeMode, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

import ArrowNavigation from "@eisinc/App/Components/XngCarousel/ArrowNavigation";
import { useSwiperHooks } from "@eisinc/App/Components/XngCarousel/hooks";
import { SwiperInstance } from "@eisinc/App/Components/XngCarousel/models";
import { xSwiperLeftShadow, xSwiperRightShadow, xSwiperStyle } from "@eisinc/App/Components/XngCarousel/styles";
import { color, mergeWithCss } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconWrapper } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import pick from "lodash/pick";
import React, { FC, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-components";

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
    lazy?: boolean;
    slideWidth?: string;
    navigationOutSide?: boolean;
    hideArrowIfDisabled?: boolean;
    shadowWidth?: string;
    withShadow?: boolean;
    shadowColor?: string;
    showArrowsOnMobile?: boolean;
    showArrowsOnTablet?: boolean;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}
            ${({ direction, height }: any) => {
                if (direction === "vertical") {
                    return css`
                        &&&& {
                            &:not(:has(.swiper-scrollbar-lock)) {
                                .arrow-button {
                                    left: 50%;
                                    transform: translateX(-50%);
                                    pointer-events: all;
                                }
                            }
                            .swiper-wrapper {
                                height: ${height ? `${height}px` : "100%"};
                            }
                            .swiper-slide {
                                &,
                                & > div {
                                    width: 100%;
                                }
                            }
                            .xng-pagination-container {
                                &:not(.visible) {
                                    display: block;
                                }
                                &.xng-arrow-navigation-container {
                                    display: block;
                                    max-height: 100%;
                                    height: 100%;
                                }
                            }
                        }
                    `;
                }
            }};

            height: 100%;
            & > div {
                height: 100%;
            }
            .swiper-wrapper {
                box-sizing: border-box;
            }
            &.with-padding {
                .swiper {
                    margin: -15px;
                    box-sizing: border-box;
                    .swiper-wrapper {
                        padding: 15px;
                    }
                }
            }

            .xng-pagination-wrapper-outside {
                position: relative;
                margin-top: 20px;
                margin-inline: auto;
                width: fit-content;
                z-index: 2;
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 12px;
                .xng-play-pause-button {
                    ${IconWrapper} {
                        &,
                        & > svg {
                            color: ${color("primary")};
                            width: 12px;
                            height: 18px;
                        }
                    }
                    &:hover {
                        ${IconWrapper} {
                            &,
                            & > svg {
                                color: ${color("common.ui8")};
                            }
                        }
                    }
                }
            }
            .xng-pagination-container {
                &:not(.visible) {
                    display: none;
                }
                position: relative;
                display: flex;
                align-items: center;
                justify-content: space-between;
                & > div,
                & > button {
                    margin: 0;
                }

                &:has(.dots-with-numbers) {
                    height: 30px;
                }

                ${({ navigationOutSide }: any) =>
                    !navigationOutSide &&
                    css`
                        position: absolute;
                        bottom: 16px;
                        left: 16px;
                    `}

                &:has(> .swiper-scrollbar-horizontal) {
                    position: relative;
                    top: unset;
                    right: unset;
                    bottom: unset;
                    left: unset;
                }

                &.xng-arrow-navigation-container {
                    left: 0;
                    ${({ scrollbar }: any) =>
                        scrollbar
                            ? css`
                                  top: calc(50% - 14px); // subtraction the scrollbar height
                              `
                            : css`
                                  top: 50%;
                              `}
                    z-index: 3;
                    justify-content: space-between;
                    width: 100%;
                    max-height: 40px;
                    margin-top: 0;
                    pointer-events: none;

                    ${({ navigationOutSide }: any) =>
                        navigationOutSide
                            ? css`
                                  bottom: 0;
                                  top: unset;
                              `
                            : css`
                                  transform: translateY(-50%);
                              `}
                }
            }

            .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                height: 8px;
                width: 100%;
                margin-top: 16px;
                position: relative;
                top: unset;
                right: unset;
                bottom: unset;
                left: unset;
                transform: initial;
                display: flex;
                align-items: center;
                background: ${color("common.ui2")};
                border-radius: 4px;
                & > .swiper-scrollbar-drag {
                    background: ${color("common.ui4")};
                    border-radius: 2px;
                    height: 4px;
                }

                &.swiper-scrollbar-lock {
                    display: none;
                }
            }

            .swiper-horizontal > .xng-pagination-container,
            .swiper-horizontal > .xng-pagination-wrapper-outside .xng-pagination-container {
                min-height: 10px;
                display: flex !important;
                gap: 4px;
            }

            ${({ isAuto, slideWidth }: any) =>
                isAuto
                    ? css`
                          .swiper-slide {
                              width: ${slideWidth};
                              height: unset;
                          }
                      `
                    : ""};

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: start;
                flex-wrap: wrap;
                gap: 4px;
                padding-block: 0px;
                &.swiper-pagination-lock {
                    display: none;
                }
                .swiper-pagination-bullet {
                    height: 10px;
                    width: 10px;
                    min-height: unset;
                    padding: 0;
                    margin: 0 4px 0 0;
                    border: 1px solid ${color("common.ui4")};
                    border-radius: 5px;
                    background: transparent;
                    opacity: 1;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("common.ui8")};
                        border-color: ${color("common.ui8")};
                        outline: none;
                    }
                    &.dots-with-numbers {
                        display: flex;
                        width: 20px;
                        height: 20px;
                        align-items: center;
                        justify-content: center;
                        font-size: 13px;
                        font-weight: 700;
                        color: ${color("common.ui4")};
                        text-align: center;
                    }
                    &.swiper-pagination-bullet-active {
                        width: 20px;
                        background: ${color("common.black")};
                    }
                }
            }
            &:has(.swiper-button-prev:not(.swiper-button-disabled)) {
                ${xSwiperLeftShadow}
            }

            &:has(.swiper-button-next:not(.swiper-button-disabled)) {
                ${xSwiperRightShadow}
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const {
        children,
        className,
        extendedStyles,
        lazy = true,
        slideWidth = "auto",
        navigationOutSide = false,
        hideArrowIfDisabled = true,
        withShadow = true,
        shadowWidth = "0px",
        shadowColor = "common.ui1",
        showArrowsOnMobile = false,
        showArrowsOnTablet = false,
        slidesPerView = "auto",
        navigation = false,
        spaceBetween = 20,
        pagination = false,
        scrollbar = false,
        draggable = false,
        ...swiperProps
    } = props;
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));
    const id = useMemo(() => uniqueId(), []);
    const swiperRef = useRef<SwiperInstance | null>(null);
    const [autoplay, setAutoplay] = useState(false);
    const { isLocked } = useSwiperHooks(swiperRef.current);

    useEffect(() => {
        setAutoplay(!!swiperProps.autoplay);
    }, [swiperProps.autoplay]);

    const handleInit = useCallback((swiper: SwiperInstance) => {
        swiperRef.current = swiper;
        swiperProps.onInit?.(swiper);
    }, []);

    let paginationOptions;
    let scrollbarOptions;

    if (pagination) {
        paginationOptions =
            typeof pagination === "boolean" ? { el: ".swiper-pagination", clickable: true } : pagination;
    }

    if (scrollbar) {
        scrollbarOptions = typeof scrollbar === "boolean" ? { draggable: true, el: ".swiper-scrollbar" } : scrollbar;
    }

    return (
        <StyledWrapper
            {...styles.container}
            className={`carousel-main-wrapper ${className || ""}`}
            {...{
                isAuto: slidesPerView === "auto" && !props.breakpoints,
                scrollbar,
                slideWidth,
                navigationOutSide,
                withShadow,
                shadowWidth,
                shadowColor,
                direction: swiperProps.direction,
                height: swiperProps.height,
            }}
        >
            {/* Add observer/observeParents in-order to re-init in case of content change  */}
            <Swiper
                {...swiperProps}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                modules={[Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay, EffectFade, FreeMode]}
                slidesPerView={slidesPerView}
                navigation
                spaceBetween={spaceBetween}
                draggable={draggable}
                observeParents
                observer
                simulateTouch={swiperProps.simulateTouch || !!draggable}
                onInit={handleInit}
            >
                {React.Children.map(children, (child, index) => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    // If the child is a Fragment, don’t add className or modify props
                    if (child.type === React.Fragment) {
                        return (
                            <SwiperSlide key={`${id}_${index}`}>
                                {child}
                                {lazy && <div className="swiper-lazy-preloader"></div>}
                            </SwiperSlide>
                        );
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as any, [...swiperSlidePropsKeys]) as any;
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);
                    childrenProps.className = `${childrenProps.className || ""} swiper-lazy`.trim();
                    return (
                        <SwiperSlide {...swiperSlideProps} key={`${id}_${index}`}>
                            {React.createElement(child.type, childrenProps)}
                            {lazy && <div className="swiper-lazy-preloader"></div>}
                        </SwiperSlide>
                    );
                })}

                {((!navigationOutSide && (pagination || navigation)) || scrollbar) && (
                    <>
                        {!navigationOutSide && (
                            <StyledWrapper className="xng-arrow-navigation-container xng-pagination-container">
                                {navigation && (
                                    <ArrowNavigation
                                        arrowType="prev"
                                        showOnMobile={showArrowsOnMobile}
                                        showOnTablet={showArrowsOnTablet}
                                        hideArrowIfDisabled={isLocked || hideArrowIfDisabled}
                                    />
                                )}
                                {navigation && (
                                    <ArrowNavigation
                                        arrowType="next"
                                        showOnMobile={showArrowsOnMobile}
                                        showOnTablet={showArrowsOnTablet}
                                        hideArrowIfDisabled={isLocked || hideArrowIfDisabled}
                                    />
                                )}
                            </StyledWrapper>
                        )}

                        <StyledWrapper className="xng-pagination-container">
                            {!navigationOutSide && pagination && <div className="swiper-pagination" />}
                            {scrollbar && <div className="swiper-scrollbar" />}
                        </StyledWrapper>
                    </>
                )}
                {navigationOutSide && (pagination || navigation) && (
                    <StyledWrapper className="xng-pagination-wrapper-outside">
                        {navigation && (
                            <ArrowNavigation
                                arrowType="prev"
                                showOnMobile={showArrowsOnMobile}
                                showOnTablet={showArrowsOnTablet}
                                hideArrowIfDisabled={isLocked || hideArrowIfDisabled}
                                navigationOutSide={navigationOutSide}
                            />
                        )}
                        <StyledWrapper className="xng-pagination-container">
                            {pagination && <div className="swiper-pagination" />}
                        </StyledWrapper>
                        {navigation && (
                            <ArrowNavigation
                                arrowType="next"
                                showOnMobile={showArrowsOnMobile}
                                showOnTablet={showArrowsOnTablet}
                                hideArrowIfDisabled={isLocked || hideArrowIfDisabled}
                                navigationOutSide={navigationOutSide}
                            />
                        )}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

export default XngCarousel;
